import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { assetApprovalChainQuery } from '@/entities/asset-approval-chain';
import { useCurrentUserId } from '@/entities/user';
import {
  approvalMenuItemsFor,
  useApprovalOverride,
  useApproveAssetStep,
  useOverrideAssetStep,
  type ApprovalMenuItem,
} from '@/features/approve-asset-step';
import { notificationService } from '@/lib/services/notification.service';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { parseProblemDetail } from '@/shared/utils/problem-details';

interface Input {
  projectId: string;
  folderId: string;
  fileId: string;
  /** From the file list's own `workflowProgress`, so a chain-less asset never fetches. */
  hasChain: boolean;
  menuOpen: boolean;
}

export interface FileApprovalActions {
  items: ApprovalMenuItem[];
  isLoading: boolean;
  isUpdating: boolean;
  onSelect: (item: ApprovalMenuItem) => void;
}

export function useFileApprovalActions(input: Input): FileApprovalActions {
  const { projectId, folderId, fileId, hasChain, menuOpen } = input;
  const { t } = useTranslation('projects');

  const enabled = useApprovalChainsEnabled();
  const currentUserId = useCurrentUserId();
  const isManager = useApprovalOverride();

  const active = enabled && hasChain;

  const { data: chain, isLoading } = useQuery({
    ...assetApprovalChainQuery(projectId, fileId),
    enabled: active && menuOpen,
  });

  const approveStep = useApproveAssetStep({ projectId, folderId });
  const overrideStep = useOverrideAssetStep({ projectId, folderId });

  const items = active ? approvalMenuItemsFor(chain, currentUserId, isManager) : [];

  const onSelect = (item: ApprovalMenuItem) => {
    if (item.kind === 'approved') return;

    const handlers = {
      onSuccess: () =>
        notificationService.showSuccess(
          t('projectDetail.fileAssetCard.stepApprovedToast', { step: item.stepName }),
        ),
      onError: (error: Error) => {
        const { detail } = parseProblemDetail(
          error,
          t('projectDetail.fileAssetCard.failedToApproveStep'),
        );
        notificationService.showError(detail);
      },
    };

    if (item.kind === 'override') {
      overrideStep.mutate({ assetId: fileId, stepId: item.stepId }, handlers);
      return;
    }

    approveStep.mutate({ assetId: fileId, stepId: item.stepId }, handlers);
  };

  return {
    items,
    isLoading: active && menuOpen && isLoading,
    isUpdating: approveStep.isPending || overrideStep.isPending,
    onSelect,
  };
}
